<template>
    <tr @click="onClick(quote.uuid)" :class="{highlightRow: quote.uuid === selectedRow}">
      <td data-label="Reference #">
        <strong>
          {{ quote.reference }}
        </strong> <br/>
        <span>Last Edited By:<br/>
          <span class="text-muted" v-if="quote.DiscountApprover && quote.DiscountApprover.fullName"><i class="far fa-user" /> {{ quote.DiscountApprover.fullName }}</span>
          <span :class="status.class">{{ status.text }}</span>
          <span  v-if="quote.unaccepted" class="text-muted">{{ unacceptedDate }}</span>
        </span><br/>
        <span v-if="quote.originalAuthor">Created By: <br/><span class="text-muted"><i class="far fa-user" /> {{ quote.originalAuthor }}</span></span>
      </td>
      <td data-label="Property Address">
        {{ quote.propertyAddressName ? quote.propertyAddressName : quote.propertyAddress }}
      </td>
      <td data-label="Quote Type">
        {{ quote.quoteName }}
      </td>
      <td data-label="Requested">
        {{ quote.ocManagerName }} <br/>
        <span v-if="quote.Company && quote.Company.name">({{ quote.Company.name }})<br/></span>
        <span class="text-muted">{{ dateRequested }}</span>
      </td>
      <td data-label="Sent">
        <template v-if="quote.sentDate">
          <template v-if="quote.EmailSender">
            {{ quote.EmailSender.fullName }} <br/>
          </template>
          <span class="text-muted">{{ dateSent }}</span><br/>
          <span :class="daysSinceSent.class">{{ daysSinceSent.text }} days since sent</span>
        </template>
        <template v-else-if="quote.acceptedDate"> 
          <strong>No quote provided </strong>to client, created in house via work order
        </template>
      </td>
      <td data-label="Accepted">
        <template v-if="quote.acceptedDate">
          <strong>Within Terms:</strong> 
          <strong><span :class="acceptedWithinQuoteTradingTerms.class">{{ acceptedWithinQuoteTradingTerms.text }}</span></strong>
          <br/>
          <strong>Accepted On:</strong> <br/>
          <span class="text-muted">{{ dateAccepted }}</span><br/>
          <strong>{{ quote.QuoteAssignedTo ? 'Assigned To:' : '' }}</strong>
          <br/>
          {{ quote.QuoteAssignedTo ? quote.QuoteAssignedTo.fullName : '' }} <br/>
          <span class="text-muted">{{ dateAssigned }}</span>
        </template>
      </td>
      <td v-if="quote.Company" data-label="Quoted Sum">
        {{ (quote.Company.serviceProviderPercent > 0) ? quote.actualCost * (1 + 0.01 * parseFloat(quote.Company.serviceProviderPercent)) 
            : (quote.Company.serviceProviderAmount > 0) ? quote.actualCost + parseFloat(quote.Company.serviceProviderAmount)
            : quote.actualCost | toDollarAmount }}
      </td>
      <td v-else data-label="Quoted Sum">
        {{ quote.actualCost | toDollarAmount }}
      </td>
      <td class="button-2 text-center">
        <router-link
          :to="{ path: '/quotes/notes/' + quote.uuid}"
          :class="quote.QuoteNotes.length > 0 ? 'my-1 btn btn-success' : 'my-1 btn btn-primary'">
          <i class="fa fa-sticky-note" /> Notes
        </router-link>
        <router-link
          :to="{ path: '/quotes/edit/' + quote.uuid}"
          class="my-1 btn btn-warning">
          <i class="far fa-fw fa-edit" /> Edit
        </router-link>
        <button v-if="quote.QuotePdf"
          class="my-1 btn btn-success"
          @click="download()">
          <i class="fa fa-download" /> Download
        </button>
        <router-link v-if="quote.QuotePdf"
          target="_blank"
          :to="{ path: '/api/public/pdf/' + quote.uuid + '?m=Quote' }"
          class="my-1 btn btn-info">
          <i class="fa fa-fw fa-search" /> Preview
        </router-link>
        <button
          v-if="quote.sentDate && !quote.acceptedDate"
          :disabled = quote.acceptedDate
          @click="requestSignature()"
          class="my-1 btn btn-primary">
          <i class="fa fa-fw fa-check-square" /> Request Signature
        </button>
        <button
          v-if="quote.sentDate || !quote.acceptedDate"
          :disabled = quote.acceptedDate
          @click="acceptQuote()"
          class="my-1 btn btn-info">
          <i class="fa fa-fw fa-check-square" /> {{ quote.acceptedDate ? 'Accepted' : 'Accept Quote' }}
        </button>
        <button
          class="my-1 btn btn-indigo"
          @click="duplicate()">
          <i class="fa fa-fw fa-copy" /> Duplicate
        </button>
        <button
          v-if="quote.QuotePdf"
          class="my-1 btn btn-primary"
          @click="send()">
          <i class="far fa-fw fa-paper-plane" /> Email
        </button>
        <button
          class="my-1 btn btn-dark"
          @click="terminateQuote()">
          <i class="fas fa-times" /> Terminate
        </button>
        <button
          class="my-1 btn btn-danger"
          @click="deleteQuote()">
          <i class="fa fa-fw fa-trash" /> Delete
        </button>
      </td>
      <form action="/api/admin/form/download" method="post" ref="form">
        <input type="hidden" name="token" v-model="token">
        <input type="hidden" name="permalink" value="downloadQuote">
        <input type="hidden" name="data" :value="formData">
      </form>
    </tr>

</template>

<script>
  import {
    ACCEPT_QUOTE,
    DELETE_QUOTE,
    DUPLICATE_QUOTE,
    SEND_QUOTE,
    TERMINATE_QUOTE,
    REQUEST_QUOTE_SIGNATURE
  } from '@/vuex/types'
  export default {
    name: 'PropertyReportQuoteRow',
    props: {
      object: {
        type: Object
      },
      onClick: {
        type: Function
      },
      selectedRow: {
        type: String,
        default: ''
      }
    },
    computed: {
      formData () {
        return JSON.stringify({ quoteUuid: this.quote.uuid })
      },
      status () {
        let status = {}
        if (this.quote.completed) {
          status.class = 'badge badge-pill badge-success'
          status.text = 'Completed'
        }
        if (this.quote.unaccepted) {
          status.class = 'badge badge-pill badge-danger'
          status.text = 'Cancelled Work Order'
        }
        return status
      },
      dateRequested () {
        return moment(this.quote.createdAt).format('dddd Do of MMMM YYYY hh:mm A')
      },
      dateSent () {
        return moment(this.quote.sentDate).format('dddd Do of MMMM YYYY hh:mm A')
      },
      dateAssigned () {
        return this.quote.assignedDate ? moment(this.quote.assignedDate).format('dddd Do of MMMM YYYY hh:mm A') : null
      },
      unacceptedDate () {
        return this.quote.unacceptedDate ? moment(this.quote.unacceptedDate).format('dddd Do of MMMM YYYY hh:mm A') : null
      },
      dateAccepted () {
        return moment(this.quote.acceptedDate).format('dddd Do of MMMM YYYY hh:mm A')
      },
      daysSinceSent () {
        let diff = {text: 'N/A', class: 'badge badge-pill badge-info'}
        if (this.quote.sentDate) {
          let difference = moment().diff(this.quote.sentDate, 'days')
          diff.text = difference
          if (!this.quote.isExpired) {
            if (difference <= 30) {
              diff.class = 'badge badge-pill badge-success'
            } else if (difference <= 60) {
              diff.class = 'badge badge-pill badge-warning'
            } else if (difference <= 90) {
              diff.class = 'badge badge-pill badge-danger'
            } else {
              diff.class = 'badge badge-pill badge-dark'
            }
          } else {
            diff.class = 'badge badge-pill badge-dark'
          }
        }
        return diff
      },
      acceptedWithinQuoteTradingTerms () {
        let termsObj = {class: 'badge-white'}
        if (this.quote.acceptedDate && this.quote.sentDate) {
          let acceptedWithinTerms = moment(this.quote.acceptedDate).diff(this.quote.sentDate, 'days') <= 60
          if (acceptedWithinTerms) {
            termsObj = {
              class: 'badge badge-pill badge-success',
              text: 'Yes'
            }
          } else {
            termsObj = {
              class: 'badge badge-pill badge-danger',
              text: 'No'
            }
          }
        }
        return termsObj
      },
      quote () {
        return {
          ...this.object
        }
      }
    },
    data () {
      return {
        token: ''
      }
    },
    methods: {
      async acceptQuote () {
        await this.$store.dispatch(ACCEPT_QUOTE, this.quote.uuid)
      },
      async requestSignature () {
        await this.$store.dispatch(REQUEST_QUOTE_SIGNATURE, this.quote.uuid)
      },
      async download () {
        this.token = window.sessionStorage.getItem('token')
        await this.$nextTick()
        this.$refs.form.submit()
      },
      async send () {
        await this.$store.dispatch(SEND_QUOTE, this.quote.uuid)
      },
      async deleteQuote () {
        await this.$store.dispatch(DELETE_QUOTE, this.quote.uuid)
      },
      async terminateQuote () {
        await this.$store.dispatch(TERMINATE_QUOTE, this.quote.uuid)
      },
      async duplicate () {
        await this.$store.dispatch(DUPLICATE_QUOTE, this.quote.uuid)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/rows/PropertyReportQuote.vue